import React, { useEffect, useState } from "react";
import fetchData from "../../api/fetchApi";
import Table from "../common/Table";
import PageHeader from "../common/PageHeader";
import ConfirmDeleteAlert from "../common/ConfirmDeleteAlert";

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, last: 1, perPage: 15, total: 0 });
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filters, setFilters] = useState({
    id: "",
    amount: "",
    type: "",
    description: "",
    reference: "",
    userable_id: ""
  });

  const fetchTransactions = async (page = 1, perPage = 15, filterObj = filters) => {
    setLoading(true);
    setError(null);
    try {
      let query = `finance/transactions?page=${page}&per_page=${perPage}`;
      if (filterObj.id) query += `&filter[id]=${encodeURIComponent(filterObj.id)}`;
      if (filterObj.amount) query += `&filter[amount]=${encodeURIComponent(filterObj.amount)}`;
      if (filterObj.type) query += `&filter[type]=${encodeURIComponent(filterObj.type)}`;
      if (filterObj.description) query += `&filter[description]=${encodeURIComponent(filterObj.description)}`;
      if (filterObj.reference) query += `&filter[reference]=${encodeURIComponent(filterObj.reference)}`;
      if (filterObj.userable_id) query += `&filter[userable_id]=${encodeURIComponent(filterObj.userable_id)}`;
      const data = await fetchData(query);
      setTransactions(data.data || []);
      setPagination({
        current: data.meta?.current_page || 1,
        last: data.meta?.last_page || 1,
        perPage: data.meta?.per_page || 15,
        total: data.meta?.total || 0,
      });
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(1, pagination.perPage, filters);
    // eslint-disable-next-line
  }, [filters]);
  const onFilter = () => {
    fetchTransactions(1, pagination.perPage, filters);
  };

  // Actions for each row
  const act = [
    {
      label: "عرض",
      onClick: async (row) => {
        try {
          const data = await fetchData(`finance/transactions/${row.id}`);
          setViewData(data);
        } catch (err) {
          setError(err.message || "حدث خطأ أثناء جلب التفاصيل");
        }
      },
    },
    {
      label: "تعديل",
      onClick: async (row) => {
        try {
          const data = await fetchData(`finance/transactions/${row.id}`);
          setEditData(data);
        } catch (err) {
          setError(err.message || "حدث خطأ أثناء جلب البيانات للتعديل");
        }
      },
    },
    {
      label: deleteLoading ? "جاري الحذف..." : "حذف",
      onClick: (row) => setDeleteId(row.id),
      disabled: deleteLoading,
    },
  ];

  // Delete handler
  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await fetchData(`finance/transactions/${deleteId}`, "DELETE");
      setDeleteId(null);
      fetchTransactions(pagination.current, pagination.perPage);
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء الحذف");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Edit handler
  const handleEditSave = async (form, setFieldError) => {
    setEditLoading(true);
    setFieldError && setFieldError(null);
    try {
      await fetchData(`finance/transactions/${editData.id}`, "PUT", form);
      setEditData(null);
      fetchTransactions(pagination.current, pagination.perPage);
    } catch (err) {
      setFieldError && setFieldError(err.message || "حدث خطأ أثناء التعديل");
    } finally {
      setEditLoading(false);
    }
  };

  const columns = [
    { header: "المعرف", accessor: "id" },
    { header: "المبلغ", accessor: "amount" },
    { header: "النوع", accessor: "type" },
    { header: "الوصف", accessor: "description" },
    { header: "تاريخ الإنشاء", accessor: "created_at" },
    { header: "المرجع", accessor: "reference" },
    { header: "المستخدم", accessor: "userable_id" },
  ];

  // View/Edit Modal
  function ViewEditModal({ open, onClose, data, onSave, loading }) {
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState(data || {});
    const [fieldError, setFieldError] = useState(null);
    useEffect(() => { setForm(data || {}); setFieldError(null); setEditMode(false); }, [data, open]);
    if (!open || !data) return null;
    // Editable fields only for edit mode
    const editableFields = ["amount", "type", "description", "reference"];
    // Helper to flatten nested objects for view
    function flattenObject(obj, parentKey = "", res = {}) {
      for (const [key, value] of Object.entries(obj)) {
        const fullKey = parentKey ? `${parentKey}.${key}` : key;
        if (typeof value === "object" && value !== null) {
          flattenObject(value, fullKey, res);
        } else {
          res[fullKey] = value;
        }
      }
      return res;
    }
  const flatFields = flattenObject(form);
  const oldFlatFields = flattenObject(data || {});
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <form onSubmit={async e => { e.preventDefault(); if (editMode) await onSave(form, setFieldError); }} className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full text-right overflow-y-auto max-h-[90vh]" dir="rtl">
          <div className="flex justify-end mb-2"><button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button></div>
          <h2 className="text-2xl font-bold mb-4">{editMode ? "تعديل الحركة المالية" : "تفاصيل الحركة المالية"}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(flatFields).map(([k, v]) => {
              const lastKey = k.split('.').slice(-1)[0];
              if (editableFields.includes(lastKey) && editMode) {
                return (
                  <div key={k} className="flex flex-col gap-1">
                    <label className="font-bold text-sm mb-1">{k}</label>
                    <input name={k} value={form[k] || ''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} className="border rounded px-3 py-2 w-full" />
                    <span className="text-xs text-gray-500 mt-1">القيمة السابقة: <span className="bg-gray-100 px-2 py-1 rounded">{oldFlatFields[k] == null ? '' : String(oldFlatFields[k])}</span></span>
                  </div>
                );
              } else {
                return (
                  <div key={k} className="flex flex-col gap-1">
                    <label className="font-bold text-sm mb-1">{k}</label>
                    <input name={k} value={v == null ? '' : String(v)} readOnly className="border rounded px-3 py-2 w-full bg-gray-100 text-gray-700" />
                  </div>
                );
              }
            })}
          </div>
          {fieldError && <div className="text-red-600 text-sm my-2">{fieldError}</div>}
          <div className="flex gap-2 justify-end mt-6">
            <button type="button" className="bg-gray-200 px-4 py-2 rounded" onClick={onClose}>إغلاق</button>
            {!editMode && <button type="button" className="bg-blue-600 text-white px-4 py-2 rounded" onClick={()=>setEditMode(true)}>تعديل</button>}
            {editMode && <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded" disabled={loading}>{loading ? "جاري الحفظ..." : "حفظ"}</button>}
            {editMode && <button type="button" className="bg-gray-300 px-4 py-2 rounded" onClick={()=>setEditMode(false)}>إلغاء التعديل</button>}
          </div>
        </form>
      </div>
    );
  }



  // Unified view/edit modal logic
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  // Open modal for view/edit
  const openModal = async (row, edit) => {
    setModalLoading(true);
    try {
      const data = await fetchData(`finance/transactions/${row.id}`);
      setModalData(data);
    } catch (err) {
      setError(err.message || "حدث خطأ أثناء جلب التفاصيل");
    } finally {
      setModalLoading(false);
    }
  };
  return (
    <div className="min-h-screen p-4 md:p-6 bg-white" dir="rtl">
      <PageHeader title="الحركات المالية" total={pagination.total} stats={[
        
      ]} />
      <Table
        columns={columns}
        rows={transactions}
        filters={filters}
        setFilters={setFilters}
        onFilter={onFilter}
        act={[
          {
            label: "عرض/تعديل",
            onClick: (row) => openModal(row, false),
          },
          {
            label: deleteLoading ? "جاري الحذف..." : "حذف",
            onClick: (row) => setDeleteId(row.id),
            disabled: deleteLoading,
          },
        ]}
        pagination={{
          current: pagination.current,
          last: pagination.last,
          perPage: pagination.perPage,
          label: `الصفحة ${pagination.current} من ${pagination.last}`,
          onPageChange: (page) => fetchTransactions(page, pagination.perPage, filters),
          onPerPageChange: (perPage) => fetchTransactions(1, perPage, filters),
        }}
      />
      {loading && <div className="text-center mt-4">جاري التحميل...</div>}
      {error && <div className="text-red-600 text-center mt-4">{error}</div>}
      <ViewEditModal open={!!modalData} onClose={()=>setModalData(null)} data={modalData} onSave={handleEditSave} loading={editLoading || modalLoading} />
      {deleteId && <ConfirmDeleteAlert
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title="تأكيد حذف الحركة"
        message="هل أنت متأكد أنك تريد حذف هذه الحركة؟ لا يمكن التراجع عن هذا الإجراء."
      />}
    </div>
  );
}
